import { defineStore } from 'pinia'
import Appearance from '@/utils/classes/Appearance'
import { decodeJWT, arrayToObject, getOwnerUsername } from '@/utils/helpers'
import { makeSetErrorsAction, makeStartFetchAction, makeStopFetchAction } from '@/utils/helpers/pinia'
import { useAuthStore } from './auth'
import { useI18nStore } from './i18n'
import { useOrganizationStore } from './organization'
import { useBillingStore } from './organization/billing'
import { useLicenceStore } from './organization/licence'
import { useProjectStore } from './organization/project'
import { useUserStore } from './user'

export const checkProductTutorials = (guide) => {
  const userStore = useUserStore()

  const isPresentationCompleted = _.get(guide, 'presentationCompleted', false)
  const isProductTourCompleted = _.get(guide, 'tour.completed', false)

  if (!isPresentationCompleted) userStore.CLEAR_PRESENTATION_COMPLETE()
  isProductTourCompleted
    ? userStore.SET_TOUR_COMPLETE()
    : userStore.CLEAR_TOUR_COMPLETE()
}

export const supportedProviders = ['azuread', 'google', 'github', 'oidc']

export const initialState = {
  apiVersion: null,
  appAppearance: null,
  appConfig: null,
  countries: [],
  errors: {
    organizations: [],
  },
  fetchInProgress: {
    rootAppAppearance: false,
  },
  // Whether a user that belongs to no organization can create one (GET /config). null until
  // fetched, which hides creation
  orgCreationAllowed: null,
  organizations: [],
  rootAppAppearance: null,
  servicesStatus: {},
}

export const useRootStore = defineStore('root', {
  state: () => _.cloneDeep(initialState),

  getters: {
    appearance: (state) => state.appAppearance?.orgCanonical ? state.appAppearance : state.rootAppAppearance,
    defaultRoute () {
      const authStore = useAuthStore()
      return authStore.defaultRoute
    },
    displayName: (state) => state.appAppearance?.orgCanonical ? state.appAppearance?.display_name : state.rootAppAppearance?.display_name,
    hasActiveBillingPlan () {
      const billingStore = useBillingStore()
      return billingStore.hasActiveBillingPlan
    },
    hasOrgs: (state) => !_.isEmpty(state.organizations),
    isAuthenticated () {
      const authStore = useAuthStore()
      return authStore.isAuthenticated
    },
    isEntityOwner () {
      return ({ owner } = {}) => {
        const authStore = useAuthStore()
        return _.isEqual(authStore.username, getOwnerUsername(owner))
      }
    },
    isOnPrem () {
      const licenceStore = useLicenceStore()
      return licenceStore.isOnPrem
    },
    isOrgAdmin () {
      const authStore = useAuthStore()
      return authStore.isOrgAdmin
    },
    isOrgSelected () {
      const orgStore = useOrganizationStore()
      return orgStore.isOrgSelected
    },
    orgCanonical () {
      const orgStore = useOrganizationStore()
      return orgStore.orgCanonical
    },
    orgMembers () {
      const orgStore = useOrganizationStore()
      return orgStore.orgMembers
    },
    orgName () {
      const orgStore = useOrganizationStore()
      return orgStore.orgName
    },
    projectCanonical () {
      const projectStore = useProjectStore()
      return projectStore.projectCanonical
    },
    projectName () {
      const projectStore = useProjectStore()
      return projectStore.projectName
    },
    requirePayment () {
      const billingStore = useBillingStore()
      return billingStore.requirePayment
    },
    socialProviders: (state) => _(state.appConfig?.authentication || {}).keys().intersection(supportedProviders).value(),
    username () {
      const authStore = useAuthStore()
      return authStore.username
    },
  },

  actions: {
    SET_ERRORS: makeSetErrorsAction(),
    START_FETCH: makeStartFetchAction(),
    STOP_FETCH: makeStopFetchAction(),

    async FETCH_API_VERSION () {
      const { data } = (await this.$cycloid.ydAPI.getAppVersion()) || {}
      if (data) this.SET_API_VERSION(data)
    },

    async FETCH_APP_APPEARANCE (orgCanonical = null) {
      if (orgCanonical) {
        const { data } = await this.$cycloid.ydAPI.getActiveAppearance(orgCanonical) || {}
        data
          ? this.SET_APP_APPEARANCE({ ...data, orgCanonical })
          : this.RESET_STATE('appAppearance')
      }

      if (!this.rootAppAppearance && !this.fetchInProgress.rootAppAppearance) {
        this.START_FETCH('rootAppAppearance')
        const { data = null } = (await this.$cycloid.ydAPI.getDefaultAppearance()) || {}
        data && this.SET_ROOT_APP_APPEARANCE(data)
        this.STOP_FETCH('rootAppAppearance')
      }
    },

    async FETCH_SERVICES_STATUS () {
      const { data } = (await this.$cycloid.ydAPI.getStatus()) || {}
      if (data) this.SET_SERVICES_STATUS(data)
    },

    async FETCH_ORG_CREATION_ALLOWED () {
      const { data } = (await this.$cycloid.ydAPI.getSentryEnable()) || {}
      this.SET_ORG_CREATION_ALLOWED(Boolean(data?.allow_org_creation))
    },

    async FETCH_COUNTRIES () {
      const { data } = (await this.$cycloid.ydAPI.getCountries()) || {}
      if (data) this.SET_COUNTRIES(data)
    },

    async FETCH_ORGS ({ reqPage } = {}) {
      const { data, errors } = (await this.$cycloid.ydAPI.getOrgs(reqPage)) || {}
      if (data) this.SET_ORGANIZATIONS(data)
      if (errors) this.SET_ERRORS({ key: 'organizations', errors })
    },

    async RESTORE_SESSION () {
      const authStore = useAuthStore()
      const userStore = useUserStore()
      authStore.RESTORE_CREDENTIALS()
      const { isAuthenticated } = authStore
      const { cycloid } = (authStore.jwt && decodeJWT(authStore.jwt.split('.')[1])) || {}
      const lsProfile = JSON.parse(_.get(localStorage, LSK.PROFILE) || '{}')
      const hasValidProfileStored = lsProfile?.created_at && _.isEqual(lsProfile.username, _.get(cycloid, 'user.username'))

      if (!isAuthenticated) {
        userStore.CLEAR_PROFILE()
        authStore.CLEAR_CREDENTIALS()
        return false
      }

      const orgStore = useOrganizationStore()
      const billingStore = useBillingStore()
      const i18nStore = useI18nStore()

      if (hasValidProfileStored) userStore.RESTORE_PROFILE()
      else await userStore.GET_PROFILE()

      checkProductTutorials(userStore.guide)

      orgStore.RESTORE_ORGANIZATION()
      billingStore.SET_ORG_BILLING_STATUS()

      const locale = _.get(userStore, 'profile.locale', null)
      if (locale) i18nStore.CHANGE_LOCALE(locale)

      return true
    },

    async UPDATE_USER_SESSION (jwt) {
      const userStore = useUserStore()
      const orgStore = useOrganizationStore()
      const billingStore = useBillingStore()
      const { cycloid: { user, organization } } = decodeJWT(jwt.split('.')[1])
      if (user) await userStore.GET_PROFILE(user)

      checkProductTutorials(userStore.guide)

      if (organization) orgStore.SET_ORGANIZATION(organization)
      else orgStore.RESET_ORGANIZATION(this.organizations)

      await billingStore.SET_ORG_BILLING_STATUS()
    },

    async GET_APP_CONFIG (orgCanonicalToLogIn) {
      const { data: platformConfig } = await this.$cycloid.ydAPI.getSentryEnable() || {}
      if (platformConfig?.sentry_enabled) {
        const { enableSentry } = await import('@/utils/plugins/sentry')
        enableSentry()
      }

      const { data, errors } = (orgCanonicalToLogIn ? await this.$cycloid.ydAPI.getOrgConfig(orgCanonicalToLogIn) : await this.$cycloid.ydAPI.getAppConfig()) || {}
      if (data) {
        const typeToProviderMap = {
          AuthenticationGitHub: 'github',
          AuthenticationGoogle: 'google',
          AuthenticationAzureAD: 'azuread',
          AuthenticationSAML: 'saml',
          AuthenticationLocal: 'email',
          AuthenticationOIDC: 'oidc',
        }

        const parsedData = data.filter((item) => item.config.enabled).map((item) => {
          item.key = typeToProviderMap[item.config.type]
          return {
            ...item.config,
            key: item.key,
          }
        })

        const objectifiedOauth = arrayToObject(parsedData, { key: 'key' })
        this.SET_APP_CONFIG({ authentication: objectifiedOauth })
      }
      if (errors) this.SET_ERRORS({ key: 'appConfig', errors })
    },

    RESET_STATE (key) {
      if (key) {
        this[key] = _.cloneDeep(initialState[key])
        return
      }

      // ! We need to retain appearance, else brand colours reset on logout
      const { appAppearance, rootAppAppearance, ...rest } = initialState
      for (const stateKey in rest) {
        this[stateKey] = _.cloneDeep(initialState[stateKey])
      }
    },

    SET_API_VERSION (version) {
      this.apiVersion = version
    },

    SET_APP_APPEARANCE ({ orgCanonical, ...appearance }) {
      this.appAppearance = new Appearance(orgCanonical, appearance)
    },

    SET_ROOT_APP_APPEARANCE (appearance) {
      this.rootAppAppearance = new Appearance(null, appearance)
    },

    SET_ORGANIZATIONS (organizations) {
      this.organizations = organizations
    },

    SET_SERVICES_STATUS (status) {
      this.servicesStatus = status
    },

    SET_ORG_CREATION_ALLOWED (allowed) {
      this.orgCreationAllowed = allowed
    },

    SET_APP_CONFIG (config) {
      this.appConfig = config
    },

    SET_COUNTRIES (countries) {
      countries = _.sortBy(countries, 'name')
      this.countries = countries
    },
  },
})
